// notifications.js
// Wrapper around react-toastify for consistent notifications throughout the app

import { toast } from 'react-toastify';

/**
 * Default toast configuration
 */
const defaultOptions = {
  position: "top-right",
  autoClose: 4000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
};

/**
 * Show success notification
 * @param {string} message - Success message to display
 * @param {object} options - Optional toast configuration
 */
export const showSuccess = (message, options = {}) => {
  toast.success(message, { ...defaultOptions, ...options });
};

/**
 * Show error notification
 * @param {string} message - Error message to display
 * @param {object} options - Optional toast configuration
 */
export const showError = (message, options = {}) => {
  toast.error(message, { ...defaultOptions, autoClose: 6000, ...options });
};

/**
 * Show info notification
 * @param {string} message - Info message to display
 * @param {object} options - Optional toast configuration
 */
export const showInfo = (message, options = {}) => {
  toast.info(message, { ...defaultOptions, ...options });
};

/**
 * Show warning notification
 * @param {string} message - Warning message to display
 * @param {object} options - Optional toast configuration
 */
export const showWarning = (message, options = {}) => {
  toast.warning(message, { ...defaultOptions, ...options });
};

/**
 * Show loading notification that can be updated later
 * @param {string} message - Loading message to display
 * @returns {string|number} Toast ID for updating
 */
export const showLoading = (message = 'Loading...') => {
  return toast.loading(message, defaultOptions);
};

/**
 * Update an existing toast notification
 * @param {string|number} toastId - Toast ID to update
 * @param {string} message - New message
 * @param {string} type - Type: 'success', 'error', 'info', 'warning'
 * @param {object} options - Optional toast configuration
 */
export const updateToast = (toastId, message, type = 'success', options = {}) => {
  toast.update(toastId, {
    render: message,
    type: type,
    isLoading: false,
    ...defaultOptions,
    ...options
  });
};

/**
 * Dismiss a specific toast
 * @param {string|number} toastId - Toast ID to dismiss
 */
export const dismissToast = (toastId) => {
  toast.dismiss(toastId);
};

/**
 * Dismiss all toasts
 */
export const dismissAll = () => {
  toast.dismiss();
};

// Export default object with all methods
export default {
  success: showSuccess,
  error: showError,
  info: showInfo,
  warning: showWarning,
  loading: showLoading,
  update: updateToast,
  dismiss: dismissToast,
  dismissAll
};
